import {
  CheckInStatus,
  CheckOutStatus,
  INTERNAL_SIGNOFF_STATUSES,
  QuoteStatuses,
  UserRoles,
} from 'types';

export enum bsColors {
  primary = 'primary',
  danger = 'danger',
  white = 'white',
}

export const STATUSES_ENUM = {
  NOT_STARTED: 'Not Started',
  ADVISOR_IN_PROGRESS: 'Advisor In Progress',
  COMPLETED: 'Completed',
  PARTS_IN_PROGRESS: 'Parts In Progress',
};

export const MpiStatus = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  COMPLETE: 'Complete',
  DRAFT: 'Draft',
} as const;

// Dashboard button color and text map
export const dashboardStatuses = {
  certsAndDotsAdvisor: {
    [INTERNAL_SIGNOFF_STATUSES.NOT_STARTED]: { advisor: { color: 'white' } },
    [INTERNAL_SIGNOFF_STATUSES.COMPLETE]: { advisor: { color: 'primary' } },
    [INTERNAL_SIGNOFF_STATUSES.TECH_IN_PROGRESS]: { advisor: { color: 'white' } },
    [INTERNAL_SIGNOFF_STATUSES.ADVISOR_IN_PROGRESS]: { advisor: { color: 'danger' } },
  },
  certsAndDotsTechnician: {
    [INTERNAL_SIGNOFF_STATUSES.NOT_STARTED]: { technician: { color: 'danger' } },
    [INTERNAL_SIGNOFF_STATUSES.COMPLETE]: { technician: { color: 'primary' } },
    [INTERNAL_SIGNOFF_STATUSES.TECH_IN_PROGRESS]: { technician: { color: 'danger' } },
    [INTERNAL_SIGNOFF_STATUSES.ADVISOR_IN_PROGRESS]: { technician: { color: 'success' } },
  },
  checkin: {
    [CheckInStatus.NOT_STARTED]: { advisor: { color: 'white' } },
    [CheckInStatus.COMPLETE]: { advisor: { color: 'success' } },
  },
  checkout: {
    [CheckOutStatus.COMPLETE]: { advisor: { color: 'success' } },
    [CheckOutStatus.NOT_STARTED]: { advisor: { color: 'white' } },
    [CheckOutStatus.PAID]: { advisor: { color: 'warning' } },
    [CheckOutStatus.SIGNED]: { advisor: { color: 'warning' } },
  },
  mpi: {
    [MpiStatus.NOT_STARTED]: {
      advisor: { color: 'white', icon: 'list' },
      parts: { color: 'white', icon: 'list' },
      technician: { color: 'danger', icon: 'list' },
    },
    [MpiStatus.IN_PROGRESS]: {
      advisor: { color: 'progress', icon: 'list' },
      parts: { color: 'progress', icon: 'list' },
      technician: { color: 'progress', icon: 'list' },
    },
    [MpiStatus.SENT]: {
      advisor: {
        color: 'success',
        customText: 'Complete',
        icon: 'list',
      },
      parts: { color: 'success', icon: 'list' },
      technician: { color: 'success', icon: 'list' },
    },
    [MpiStatus.VIEWED]: {
      advisor: { color: 'success', icon: 'list' },
      parts: { color: 'success', icon: 'list' },
      technician: { color: 'success', icon: 'list' },
    },
    [MpiStatus.COMPLETE]: {
      advisor: { color: 'success', icon: 'list' },
      parts: { color: 'success', icon: 'list' },
      technician: { color: 'success', icon: 'list' },
    },
    [MpiStatus.DRAFT]: {
      advisor: { color: bsColors.white, icon: 'list' },
      parts: { color: bsColors.white, icon: 'list' },
      technician: { color: bsColors.danger, icon: 'list' },
    },
  },
  video: {
    'Not Started': {
      advisor: { color: 'white', icon: 'video' },
      parts: { color: 'white', icon: 'video' },
      technician: { color: 'danger', icon: 'video' },
    },
    Uploaded: {
      advisor: { color: 'success', icon: 'video' },
      parts: { color: 'success', icon: 'video' },
      technician: { color: 'success', icon: 'video' },
    },
    Sent: {
      advisor: { color: 'success', icon: 'video' },
      parts: { color: 'success', icon: 'video' },
      technician: { color: 'success', icon: 'video' },
    },
    Viewed: {
      advisor: { color: 'success', icon: 'video', fill: 'success' },
      parts: { color: 'success', icon: 'video', fill: 'success' },
      technician: { color: 'success', icon: 'video', fill: 'success' },
    },
  },
  iPay: {
    Confirmed: {
      advisor: { color: 'success', icon: 'dollar' },
    },
    Created: {
      advisor: { color: 'danger', icon: 'dollar' },
    },
    'Not Started': {
      advisor: { color: 'white', icon: 'dollar' },
    },
    Paid: {
      advisor: { color: 'success', icon: 'dollar' },
    },
    Refunded: {
      advisor: { color: 'danger', icon: 'dollar' },
    },
    Sent: {
      advisor: { color: 'success', icon: 'dollar' },
    },
    Viewed: {
      advisor: { color: 'success', icon: 'dollar' },
    },
  },
  quote: {
    [QuoteStatuses.NOT_STARTED]: {
      technician: { color: 'danger', icon: 'file' },
      parts: { color: 'white', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.TECHNICIAN,
        UserRoles.PARTS_ADVISOR,
      ],
    },
    [QuoteStatuses.ADVISOR_IN_PROGRESS]: {
      technician: { color: 'success', icon: 'file' },
      parts: { color: 'success', icon: 'file' },
      advisor: { color: 'danger', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.TECHNICIAN_IN_PROGRESS]: {
      technician: { color: 'danger', icon: 'file' },
      parts: { color: 'white', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_MANAGER,
        UserRoles.TECHNICIAN,
        UserRoles.PARTS_ADVISOR,
      ],
    },
    [QuoteStatuses.PARTS_IN_PROGRESS]: {
      technician: { color: 'success', icon: 'file' },
      parts: { color: 'danger', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
        UserRoles.SERVICE_MANAGER,
      ],
    },
    [QuoteStatuses.SENT]: {
      technician: { color: 'success', icon: 'file' },
      parts: { color: 'success', icon: 'file' },
      advisor: { color: 'success', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.VIEWED]: {
      technician: { color: 'success', icon: 'file', fill: 'success' },
      parts: { color: 'success', icon: 'file', fill: 'success' },
      advisor: { color: 'success', icon: 'file', fill: 'success' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.COMPLETED]: {
      technician: {
        color: 'warning',
        customText: 'Replied',
        icon: 'file',
      },
      parts: {
        color: 'warning',
        customText: 'Replied',
        icon: 'file',
      },
      advisor: {
        color: 'warning',
        customText: 'Replied',
        icon: 'file',
      },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.CONFIRMED]: {
      technician: {
        color: 'primary',
        customText: 'Complete',
        icon: 'file',
      },
      parts: {
        color: 'primary',
        customText: 'Complete',
        icon: 'file',
      },
      advisor: {
        color: 'primary',
        customText: 'Complete',
        icon: 'file',
      },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
  },
  pickList: {
    Pick: {
      technician: { color: 'danger' },
      parts: { color: 'danger' },
    },
    Picked: {
      technician: { color: 'warning' },
      parts: { color: 'warning' },
    },
    'Picked Up': {
      technician: { color: 'success' },
      parts: { color: 'success' },
    },
  },
};
Object.freeze(dashboardStatuses);

// Service Express icons color and text map
export const serviceExpressStatuses = {
  mpi: {
    'Not Started': {
      advisor: { color: 'white', icon: 'list' },
      parts: { color: 'white', icon: 'list' },
      technician: { color: 'white', icon: 'list' },
    },
    'In Progress': {
      advisor: { color: 'white', icon: 'list' },
      parts: { color: 'white', icon: 'list' },
      technician: { color: 'white', icon: 'list' },
    },
    Sent: {
      advisor: {
        color: 'white',
        icon: 'list',
      },
      parts: { color: 'white', icon: 'list' },
      technician: { color: 'white', icon: 'list' },
    },
    Viewed: {
      advisor: { color: 'white', icon: 'list' },
      parts: { color: 'white', icon: 'list' },
      technician: { color: 'white', icon: 'list' },
    },
    Complete: {
      advisor: { color: 'success', icon: 'list' },
      parts: { color: 'success', icon: 'list' },
      technician: { color: 'success', icon: 'list' },
    },
  },
  video: {
    'Not Started': {
      advisor: { color: 'white', icon: 'video' },
      parts: { color: 'white', icon: 'video' },
      technician: { color: 'white', icon: 'video' },
    },
    Uploaded: {
      advisor: { color: 'white', icon: 'video' },
      parts: { color: 'white', icon: 'video' },
      technician: { color: 'white', icon: 'video' },
    },
    Sent: {
      advisor: { color: 'white', icon: 'video' },
      parts: { color: 'white', icon: 'video' },
      technician: { color: 'white', icon: 'video' },
    },
    Viewed: {
      advisor: { color: 'success', icon: 'video', fill: 'success' },
      parts: { color: 'success', icon: 'video', fill: 'success' },
      technician: { color: 'success', icon: 'video', fill: 'success' },
    },
  },
  iPay: {
    Confirmed: {
      advisor: { color: 'success', icon: 'dollar' },
    },
    Created: {
      advisor: { color: 'white', icon: 'dollar' },
    },
    'Not Started': {
      advisor: { color: 'white', icon: 'dollar' },
    },
    Paid: {
      advisor: { color: 'white', icon: 'dollar' },
    },
    Refunded: {
      advisor: { color: 'white', icon: 'dollar' },
    },
    Sent: {
      advisor: { color: 'white', icon: 'dollar' },
    },
    Viewed: {
      advisor: { color: 'white', icon: 'dollar' },
    },
  },
  quote: {
    [QuoteStatuses.NOT_STARTED]: {
      technician: { color: 'white', icon: 'file' },
      parts: { color: 'white', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.TECHNICIAN,
        UserRoles.PARTS_ADVISOR,
      ],
    },
    [QuoteStatuses.ADVISOR_IN_PROGRESS]: {
      technician: { color: 'white', icon: 'file' },
      parts: { color: 'white', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.TECHNICIAN_IN_PROGRESS]: {
      technician: { color: 'white', icon: 'file' },
      parts: { color: 'white', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_MANAGER,
        UserRoles.TECHNICIAN,
        UserRoles.PARTS_ADVISOR,
      ],
    },
    [QuoteStatuses.PARTS_IN_PROGRESS]: {
      technician: { color: 'white', icon: 'file' },
      parts: { color: 'white', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
        UserRoles.SERVICE_MANAGER,
      ],
    },
    [QuoteStatuses.SENT]: {
      technician: { color: 'white', icon: 'file' },
      parts: { color: 'white', icon: 'file' },
      advisor: { color: 'white', icon: 'file' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.VIEWED]: {
      technician: { color: 'white', icon: 'file', fill: 'white' },
      parts: { color: 'white', icon: 'file', fill: 'white' },
      advisor: { color: 'white', icon: 'file', fill: 'white' },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.COMPLETED]: {
      technician: {
        color: 'success',
        customText: 'Replied',
        icon: 'file',
      },
      parts: {
        color: 'success',
        customText: 'Replied',
        icon: 'success',
      },
      advisor: {
        color: 'success',
        customText: 'Replied',
        icon: 'file',
      },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
    [QuoteStatuses.CONFIRMED]: {
      technician: {
        color: 'success',
        customText: 'Complete',
        icon: 'file',
      },
      parts: {
        color: 'success',
        customText: 'Complete',
        icon: 'file',
      },
      advisor: {
        color: 'success',
        customText: 'Complete',
        icon: 'file',
      },
      permissions: [
        UserRoles.ADMIN,
        UserRoles.SERVICE_ADVISOR,
        UserRoles.SERVICE_MANAGER,
        UserRoles.PARTS_ADVISOR,
        UserRoles.TECHNICIAN,
      ],
    },
  },
};
Object.freeze(serviceExpressStatuses);
